import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { subMonths, format } from "date-fns";

export interface ClassStats {
  classId: string;
  title: string;
  instructor: string;
  totalSubscriptions: number;
  attendanceRate: number;
}

export interface GymClassesData {
  popularClasses: ClassStats[];
  totalClasses: number;
  averageAttendance: number;
}

export const useGymClasses = (gymId: string) => {
  return useQuery({
    queryKey: ["gym-classes", gymId],
    queryFn: async (): Promise<GymClassesData> => {
      const now = new Date();
      const oneMonthAgo = format(subMonths(now, 1), "yyyy-MM-dd");

      // Get classes from this gym
      const { data: classes } = await supabase
        .from("classes")
        .select(`
          id,
          title,
          instructor,
          class_subscriptions(count)
        `)
        .eq("gym_id", gymId);

      const totalClasses = classes?.length || 0;

      // Get attendance data for these classes
      const classIds = classes?.map(c => c.id) || [];
      
      const { data: attendance } = await supabase
        .from("class_attendance")
        .select("class_id, attended")
        .in("class_id", classIds.length > 0 ? classIds : ['no-classes'])
        .gte("date", oneMonthAgo);

      // Calculate stats per class
      const popularClasses: ClassStats[] = (classes || []).map(cls => {
        const classAttendance = attendance?.filter(a => a.class_id === cls.id) || [];
        const totalAttendance = classAttendance.length;
        const attendedCount = classAttendance.filter(a => a.attended).length;
        const attendanceRate = totalAttendance > 0 ? (attendedCount / totalAttendance) * 100 : 0;

        return {
          classId: cls.id,
          title: cls.title,
          instructor: cls.instructor,
          totalSubscriptions: cls.class_subscriptions?.[0]?.count || 0,
          attendanceRate,
        };
      });

      // Sort by total subscriptions descending
      popularClasses.sort((a, b) => b.totalSubscriptions - a.totalSubscriptions);

      // Calculate average attendance across all classes
      const totalAttendanceRecords = attendance?.length || 0;
      const totalAttended = attendance?.filter(a => a.attended).length || 0;
      const averageAttendance = totalAttendanceRecords > 0 
        ? (totalAttended / totalAttendanceRecords) * 100 
        : 0;

      return {
        popularClasses: popularClasses.slice(0, 5), // Top 5
        totalClasses,
        averageAttendance,
      };
    },
    enabled: !!gymId,
  });
};
